import React, { useState } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import { Pause, Play } from '@phosphor-icons/react'
import { Button, Dialog, DialogContent, DialogOverlay, Icon } from '@shopflo/ui'
import { apiURI, constants } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'

interface PauseReportDialogProps {
  isOpen: boolean
  onClose: () => void
  type: 'pause' | 'resume'
  reportId: string
  onSuccess?: () => void
}

const COPY = {
  pause: {
    icon: Pause,
    title: 'Pause scheduled report?',
    description: "This report won't run again until you resume the schedule.",
    confirm: 'Pause schedule',
    action: 'pause'
  },
  resume: {
    icon: Play,
    title: 'Resume scheduled report?',
    description: 'This report will run again on its schedule.',
    confirm: 'Resume schedule',
    action: 'start'
  }
} as const

const PauseReportDialog: React.FC<PauseReportDialogProps> = ({ isOpen, onClose, type, reportId, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const copy = COPY[type]
  const isPause = type === 'pause'

  const handleConfirm = async () => {
    if (!reportId) return
    try {
      setIsSubmitting(true)
      await axios.put(
        '/dashboard/report/scheduler-status',
        { reportId, action: copy.action },
        { baseURL: apiURI.CHECKOUT_NODE, headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' } }
      )
      successToast(isPause ? 'Report paused' : 'Report resumed')
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('pause/resume failed', error)
      errorToast(isPause ? 'Failed to pause report' : 'Failed to resume report')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogOverlay className='fixed inset-0 z-40 bg-black/40' />
      <DialogContent className='z-50 w-[380px] max-w-[92vw] gap-0 overflow-hidden !rounded-2xl bg-white p-6 shadow-modal'>
        <div className='flex flex-col items-center text-center'>
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${isPause ? 'bg-red-50' : 'bg-emerald-50'}`}>
            <Icon
              IconComponent={copy.icon}
              size='md'
              className={isPause ? 'text-red-600' : 'text-emerald-600'}
              iconProps={{ weight: 'fill' }}
            />
          </div>
          <h2 className='mt-4 text-text-md-semibold text-zinc-900'>{copy.title}</h2>
          <p className='mt-1 text-text-sm-regular text-zinc-500'>{copy.description}</p>
        </div>

        <div className='mt-6 flex items-center gap-3'>
          <Button variant='outline' size='md' className='flex-1' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={isPause ? 'error' : 'primary'}
            size='md'
            className='flex-1'
            loading={isSubmitting}
            onClick={handleConfirm}
          >
            {copy.confirm}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PauseReportDialog
